import { useState, useEffect, useRef } from 'react'
import {
  X, Upload, Plus, Trash2, Building2,
  Home as HomeIcon, Tag, Palette, MessageSquare, Globe, ShoppingBag, Shield, Mail,
  CheckCircle, FileText, Eye, Mic, Scale, Image, Sparkles,
} from 'lucide-react'
import { advertiserApi, Advertiser } from '../../lib/api/advertisers'
import { AdvertiserMultiSelect } from '../../components/advertisers/AdvertiserMultiSelect'
import {
  BrandKit,
  BrandKitFormData,
  Brand,
  BrandLogo,
  BrandProperty,
  BrandDisclaimer,
  BrandTrademark,
  BrandTone,
  BrandColors,
  BrandAsset,
  VisualGuidelines,
  VoiceSynthesis,
  BrandRights,
  createEmptyBrandKitFormData,
  createEmptyBrand,
  createEmptyVisualGuidelines,
  createEmptyRights,
  createEmptyVoiceSynthesis,
  brandDataToFormData,
  LocalizedName,
  KellerType,
  HouseArchitecture,
} from '../../types/brandKit'

interface CreateBrandKitModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: BrandKitFormData) => Promise<void>
  isLoading?: boolean
  brandKit?: BrandKit | null
}

/* ------------------------------------------------------------------ */
/*  Tab definition                                                     */
/* ------------------------------------------------------------------ */
interface TabDef {
  id: string
  label: string
  icon: React.ReactNode
  adminOnly?: boolean
}

const TAB_DEFS: TabDef[] = [
  // Organisation Access is available to both roles now. The
  // `AdvertiserMultiSelect` below queries `advertiserApi.getAdvertisers()`
  // which is already BE role-scoped — admins see every advertiser, a
  // regular user only sees the ones they have access to. Same picker
  // component, correct scope for free.
  { id: 'org_access', label: 'Organisation Access', icon: <Building2 className="w-4 h-4" /> },
  { id: 'house', label: 'House Information', icon: <HomeIcon className="w-4 h-4" /> },
  { id: 'brand_identity', label: 'Brand Identity', icon: <Tag className="w-4 h-4" /> },
  { id: 'visual_identity', label: 'Visual Identity', icon: <Palette className="w-4 h-4" /> },
  { id: 'visual_guidelines', label: 'Visual Guidelines', icon: <Eye className="w-4 h-4" /> },
  { id: 'tone_voice', label: 'Tone & Voice', icon: <MessageSquare className="w-4 h-4" /> },
  { id: 'voice_synthesis', label: 'Voice Synthesis', icon: <Mic className="w-4 h-4" /> },
  { id: 'digital_properties', label: 'Digital Properties', icon: <Globe className="w-4 h-4" /> },
  { id: 'assets', label: 'Assets', icon: <Image className="w-4 h-4" /> },
  { id: 'product_catalog', label: 'Product Catalog', icon: <ShoppingBag className="w-4 h-4" /> },
  { id: 'legal', label: 'Legal & Compliance', icon: <Shield className="w-4 h-4" /> },
  { id: 'rights', label: 'Rights & Usage', icon: <Scale className="w-4 h-4" /> },
  { id: 'contact', label: 'Contact', icon: <Mail className="w-4 h-4" /> },
]

/* ------------------------------------------------------------------ */
/*  Tag / List Input                                                   */
/* ------------------------------------------------------------------ */
function TagInput({
  label,
  values,
  onChange,
  placeholder,
}: {
  label?: string
  values: string[]
  onChange: (v: string[]) => void
  placeholder?: string
}) {
  const [input, setInput] = useState('')

  const add = () => {
    const trimmed = input.trim()
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed])
      setInput('')
    }
  }

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
      )}
      <div className="flex flex-wrap gap-1.5 mb-2">
        {values.map((v, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs"
          >
            {v}
            <button type="button" onClick={() => onChange(values.filter((_, idx) => idx !== i))} className="hover:text-red-500">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add() } }}
          placeholder={placeholder}
          className="flex-1 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <button type="button" onClick={add} className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors">
          Add
        </button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Helper: simple text field                                          */
/* ------------------------------------------------------------------ */
function Field({ label, value, onChange, placeholder, required, type = 'text', maxLength, disabled }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; required?: boolean; type?: string; maxLength?: number; disabled?: boolean
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} disabled={disabled} maxLength={maxLength}
        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors disabled:opacity-50" />
    </div>
  )
}

function SelectField({ label, value, onChange, options, required }: {
  label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; required?: boolean
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Main Modal                                                         */
/* ------------------------------------------------------------------ */
export function CreateBrandKitModal({ isOpen, onClose, onSubmit, isLoading = false, brandKit = null }: CreateBrandKitModalProps) {
  const isEditMode = !!brandKit

  const [formData, setFormData] = useState<BrandKitFormData>(createEmptyBrandKitFormData())
  const [selectedAdvertisers, setSelectedAdvertisers] = useState<Advertiser[]>([])
  const [error, setError] = useState<string | null>(null)
  // Both admin and user roles now start on Organisation Access so
  // advertiser scoping is the very first thing they set. The picker
  // itself only surfaces advertisers they can see.
  const [activeTab, setActiveTab] = useState<string>('org_access')
  const [importedFileName, setImportedFileName] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (brandKit) {
      const converted = brandDataToFormData(brandKit)
      setFormData(converted)

      // Resolve advertiser_ids → full Advertiser objects for the multi-select
      if (brandKit.advertiser_ids?.length) {
        Promise.all(
          brandKit.advertiser_ids.map(async (id) => {
            try {
              const adv = await advertiserApi.getAdvertiser(parseInt(id, 10))
              return adv
            } catch {
              return { id: parseInt(id, 10), name: `Advertiser ${id}` } as Advertiser
            }
          })
        ).then(setSelectedAdvertisers)
      } else {
        setSelectedAdvertisers([])
      }
    } else {
      setFormData(createEmptyBrandKitFormData())
      setSelectedAdvertisers([])
    }
    setError(null)
    setImportedFileName(null)
    setActiveTab('org_access')
  }, [brandKit, isOpen])

  /* ---- helpers to update nested state ---- */
  const updateHouse = (patch: Partial<typeof formData.house>) =>
    setFormData((prev) => ({ ...prev, house: { ...prev.house, ...patch } }))

  const updateBrand = (index: number, patch: Partial<Brand>) =>
    setFormData((prev) => {
      const brands = [...prev.brands]
      brands[index] = { ...brands[index], ...patch }
      return { ...prev, brands }
    })

  const brand = formData.brands[0] ?? createEmptyBrand()

  const updateBrandColors = (patch: Partial<BrandColors>) =>
    updateBrand(0, { colors: { ...brand.colors, ...patch } })

  const getBrandTone = (): BrandTone => {
    if (!brand.tone || typeof brand.tone === 'string') {
      return { voice: typeof brand.tone === 'string' ? brand.tone : '', attributes: [], dos: [], donts: [] }
    }
    return brand.tone
  }

  const updateBrandTone = (patch: Partial<BrandTone>) =>
    updateBrand(0, { tone: { ...getBrandTone(), ...patch } })

  const getVisualGuidelines = (): VisualGuidelines =>
    brand.visual_guidelines || createEmptyVisualGuidelines()

  const updateVisualGuidelines = (patch: Partial<VisualGuidelines>) =>
    updateBrand(0, { visual_guidelines: { ...getVisualGuidelines(), ...patch } })

  const getVoiceSynthesis = (): VoiceSynthesis =>
    brand.voice_synthesis || createEmptyVoiceSynthesis()

  const updateVoiceSynthesis = (patch: Partial<VoiceSynthesis>) =>
    updateBrand(0, { voice_synthesis: { ...getVoiceSynthesis(), ...patch } })

  const getRights = (): BrandRights =>
    brand.rights || createEmptyRights()

  const updateRights = (patch: Partial<BrandRights>) =>
    updateBrand(0, { rights: { ...getRights(), ...patch } })

  /* ---- JSON import ---- */
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const text = ev.target?.result as string
        const json = JSON.parse(text)
        applyBrandJson(json)
        setImportedFileName(file.name)
      } catch { setError('Invalid JSON file. Please upload a valid brand.json.') }
    }
    reader.readAsText(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  /**
   * Derive a slug-style brand ID from a name.
   * "McDonald's" → "mcdonalds"
   */
  const slugify = (text: string): string =>
    text.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')

  /**
   * Extract domain from a URL string.
   * "https://www.mcdonalds.com/path" → "mcdonalds.com"
   */
  const extractDomain = (urlStr: string): string => {
    try {
      const host = new URL(urlStr).hostname
      return host.replace(/^www\./, '')
    } catch {
      return ''
    }
  }

  /**
   * Apply a brand.json to the form. Supports two shapes:
   *
   * 1. Full AdCP structure — `{ house, brands[], contact, ... }`
   * 2. Flat AdCP v3 brand — `{ name, url, logos, colors, tone, ... }`
   *    (like the sample McDonald's brand_manifest.json)
   */
  const applyBrandJson = (json: any) => {
    setError(null)

    if (Array.isArray(json.brands)) {
      applyFullStructure(json)
    } else {
      applyFlatBrand(json)
    }
  }

  /** Handle `{ house, brands[], ... }` structure */
  const applyFullStructure = (json: any) => {
    const patch: Partial<BrandKitFormData> = {}

    if (json.house && typeof json.house === 'object') {
      patch.house = {
        domain: json.house.domain || '',
        name: json.house.name || '',
        architecture: json.house.architecture || 'branded_house',
      }
      if (json.house.name) patch.name = json.house.name
    }

    if (Array.isArray(json.brands) && json.brands.length > 0) {
      const brands = json.brands.map((b: any) => mapBrandObject(b))
      patch.brands = brands
      if (!patch.name && brands[0]?.names?.[0]) {
        const firstNameObj = brands[0].names[0] as LocalizedName
        patch.name = Object.values(firstNameObj)[0] || ''
      }
    }

    if (json.contact) patch.contact = json.contact
    if (json.trademarks) patch.trademarks = json.trademarks
    if (json.authorized_operators) patch.authorized_operators = json.authorized_operators

    setFormData((prev) => ({ ...prev, ...patch }))
  }

  /**
   * Handle flat AdCP v3 brand.json — top-level keys are brand fields.
   * Maps them into the form's house + brands[0] structure.
   */
  const applyFlatBrand = (json: any) => {
    const firstNameVal = Array.isArray(json.names) && json.names.length > 0
      ? Object.values(json.names[0] as Record<string, string>)[0] || ''
      : ''
    const brandName: string = json.name || firstNameVal || ''
    const brandUrl: string = json.url || ''
    const domain = json.house?.domain || extractDomain(brandUrl)
    const houseName = json.house?.name || brandName
    const brandId = json.brand_id || json.id || slugify(brandName)

    const patch: Partial<BrandKitFormData> = {
      name: brandName || houseName,
      house: {
        domain: domain,
        name: houseName,
        architecture: json.house?.architecture || 'branded_house',
      },
      brands: [
        mapBrandObject({
          ...json,
          id: brandId,
          names: json.names || [{ en: brandName }],
          url: brandUrl,
        }),
      ],
    }

    if (json.contact) patch.contact = json.contact
    if (json.trademarks) patch.trademarks = json.trademarks
    if (json.authorized_operators) patch.authorized_operators = json.authorized_operators

    setFormData((prev) => ({ ...prev, ...patch }))
  }

  /** Normalize any brand-shaped object into the Brand interface */
  const mapBrandObject = (b: any): Brand => ({
    id: b.brand_id || b.id || '',
    url: b.url || '',
    names: b.names || [{ en: '' }],
    keller_type: b.keller_type || 'master',
    parent_brand: b.parent_brand || '',
    description: b.description || '',
    industry: b.industry || '',
    target_audience: b.target_audience || '',
    tagline: b.tagline || '',
    logos: Array.isArray(b.logos) ? b.logos : [],
    colors: b.colors || {},
    fonts: b.fonts || {},
    visual_guidelines: b.visual_guidelines || undefined,
    tone: b.tone || { voice: '', attributes: [], dos: [], donts: [] },
    voice_synthesis: b.voice_synthesis || undefined,
    properties: Array.isArray(b.properties) ? b.properties : [],
    product_catalog: b.product_catalog || undefined,
    privacy_policy_url: b.privacy_policy_url || '',
    disclaimers: normalizeDisclaimersForForm(b.disclaimers),
    assets: Array.isArray(b.assets) ? b.assets : [],
    rights: b.rights || undefined,
    context: b.context || undefined,
    avatar: b.avatar || undefined,
    brand_agent: b.brand_agent || undefined,
    contact: b.contact || undefined,
  })

  const normalizeDisclaimersForForm = (disclaimers: any): BrandDisclaimer[] => {
    if (!Array.isArray(disclaimers)) return []
    return disclaimers.map((d: any) =>
      typeof d === 'string'
        ? { text: d, context: '', required: true }
        : { text: d.text || '', context: d.context || '', required: d.required ?? true }
    )
  }

  /* ---- submit ---- */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    // At least one advertiser is required regardless of role — a
    // brand kit with no advertiser assignment isn't visible in the
    // BrandKitPicker (non-admin filters by advertiser_id) and clutters
    // the "All" empty state on the list page. Users only see their
    // own advertisers here so this is a safe universal validation.
    if (selectedAdvertisers.length === 0) { setError('Please select at least one advertiser'); setActiveTab('org_access'); return }
    if (!formData.name.trim()) { setError('Brand Kit name is required'); setActiveTab('house'); return }
    if (!formData.house.domain.trim()) { setError('House domain is required'); setActiveTab('house'); return }
    if (!formData.house.name.trim()) { setError('House name is required'); setActiveTab('house'); return }
    const firstBrand = formData.brands[0]
    if (!firstBrand?.id?.trim()) { setError('Brand ID is required'); setActiveTab('brand_identity'); return }
    const firstName = firstBrand.names?.[0]
    if (!firstName || !Object.values(firstName)[0]?.trim()) { setError('Brand name is required'); setActiveTab('brand_identity'); return }
    try {
      await onSubmit({ ...formData, advertiser_ids: selectedAdvertisers.map((a) => a.id) })
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isEditMode ? 'update' : 'create'} brand kit`)
    }
  }

  const handleClose = () => {
    if (!isLoading) { setFormData(createEmptyBrandKitFormData()); setSelectedAdvertisers([]); setError(null); setImportedFileName(null); onClose() }
  }

  if (!isOpen) return null

  /* ---- derived helpers ---- */
  const brandNames = brand.names || [{ en: '' }]
  const englishName = (brandNames[0] as LocalizedName)?.en ?? Object.values(brandNames[0] || {})[0] ?? ''
  const logos: BrandLogo[] = brand.logos || []
  const assets: BrandAsset[] = brand.assets || []
  const properties: BrandProperty[] = brand.properties || []
  const disclaimers: BrandDisclaimer[] = brand.disclaimers || []
  const trademarks: BrandTrademark[] = formData.trademarks || []
  const tone = getBrandTone()
  const colors = brand.colors || {}
  const colorVal = (v: string | string[] | undefined): string => (Array.isArray(v) ? v[0] || '#000000' : v || '#000000')

  // No tab is admin-only today (Organisation Access is available to
  // both roles — the picker itself is BE-scoped). Left the `adminOnly`
  // field on TabDef in case a future admin-only tab needs it.
  const visibleTabs = TAB_DEFS.filter((t) => !t.adminOnly)

  /* ================================================================ */
  /*  Tab content renderers                                            */
  /* ================================================================ */

  const renderOrgAccess = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Organisation Access</h3>
      <p className="text-sm text-gray-500">Select which advertisers can access this brand kit.</p>
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Advertisers <span className="text-red-500">*</span>
        </label>
        <AdvertiserMultiSelect selected={selectedAdvertisers} onChange={setSelectedAdvertisers} disabled={isLoading} />
        <p className="mt-1 text-xs text-gray-500">Selected advertisers will have access to this brand kit.</p>
      </div>
    </div>
  )

  const renderHouse = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">House Information</h3>
      <Field label="Brand Kit Name" value={formData.name} onChange={(v) => setFormData((p) => ({ ...p, name: v }))} placeholder="e.g. Nike Brand Kit" required maxLength={128} disabled={isLoading} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="House Domain" value={formData.house.domain} onChange={(v) => updateHouse({ domain: v })} placeholder="e.g. nikeinc.com" required disabled={isLoading} />
        <Field label="House Name" value={formData.house.name} onChange={(v) => updateHouse({ name: v })} placeholder="e.g. Nike, Inc." required disabled={isLoading} />
      </div>
      <SelectField label="Architecture" value={formData.house.architecture || 'branded_house'} onChange={(v) => updateHouse({ architecture: v as HouseArchitecture })}
        options={[
          { value: 'branded_house', label: 'Branded House (e.g. Google)' },
          { value: 'house_of_brands', label: 'House of Brands (e.g. P&G)' },
          { value: 'hybrid', label: 'Hybrid (e.g. Nike)' },
        ]}
      />
    </div>
  )

  const renderBrandIdentity = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Brand Identity</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Brand ID" value={brand.id} onChange={(v) => updateBrand(0, { id: v })} placeholder="e.g. nike" required disabled={isLoading} />
        <Field label="Brand Name (English)" value={englishName} onChange={(v) => { const n = [...brandNames]; n[0] = { en: v }; updateBrand(0, { names: n }) }} placeholder="e.g. Nike" required disabled={isLoading} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SelectField label="Keller Type" value={brand.keller_type || 'master'} onChange={(v) => updateBrand(0, { keller_type: v as KellerType })}
          options={[
            { value: 'master', label: 'Master Brand' }, { value: 'sub_brand', label: 'Sub-Brand' },
            { value: 'endorsed', label: 'Endorsed Brand' }, { value: 'independent', label: 'Independent Brand' },
          ]}
        />
        <Field label="Parent Brand" value={brand.parent_brand || ''} onChange={(v) => updateBrand(0, { parent_brand: v })} placeholder="e.g. nike" disabled={isLoading} />
      </div>
      <Field label="Brand URL" value={brand.url || ''} onChange={(v) => updateBrand(0, { url: v })} placeholder="https://nike.com" disabled={isLoading} />
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
        <textarea value={brand.description || ''} onChange={(e) => updateBrand(0, { description: e.target.value })} placeholder="Describe the brand..." rows={3} disabled={isLoading}
          className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors disabled:opacity-50 resize-none" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Industry" value={brand.industry || ''} onChange={(v) => updateBrand(0, { industry: v })} placeholder="e.g. retail" disabled={isLoading} />
        <Field label="Target Audience" value={brand.target_audience || ''} onChange={(v) => updateBrand(0, { target_audience: v })} placeholder="e.g. 18-35 active lifestyle" disabled={isLoading} />
      </div>
      <Field label="Tagline" value={brand.tagline || ''} onChange={(v) => updateBrand(0, { tagline: v })} placeholder="e.g. Just Do It" disabled={isLoading} />
    </div>
  )

  const renderVisualIdentity = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Visual Identity</h3>

      {/* Colors */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Colors</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {(['primary', 'secondary', 'accent', 'background', 'text'] as const).map((role) => (
            <div key={role}>
              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1 capitalize">{role}</label>
              <div className="flex items-center gap-2">
                <input type="color" value={colorVal((colors as any)[role])} onChange={(e) => updateBrandColors({ [role]: e.target.value })}
                  className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600 cursor-pointer" />
                <input type="text" value={colorVal((colors as any)[role])} onChange={(e) => updateBrandColors({ [role]: e.target.value })}
                  className="flex-1 min-w-0 px-2 py-1 text-xs border border-gray-200 dark:border-gray-800 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-mono" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fonts */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Fonts</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Primary Font" value={brand.fonts?.primary || ''} onChange={(v) => updateBrand(0, { fonts: { ...brand.fonts, primary: v } })} placeholder="e.g. Helvetica Neue" disabled={isLoading} />
          <Field label="Secondary Font" value={brand.fonts?.secondary || ''} onChange={(v) => updateBrand(0, { fonts: { ...brand.fonts, secondary: v } })} placeholder="e.g. Georgia" disabled={isLoading} />
        </div>
        <div className="mt-3">
          <TagInput label="Font URLs" values={brand.fonts?.font_urls || []} onChange={(v) => updateBrand(0, { fonts: { ...brand.fonts, font_urls: v } })} placeholder="e.g. https://fonts.example.com/font.woff2" />
        </div>
      </div>

      {/* Logos */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Logos</label>
          <button type="button" onClick={() => updateBrand(0, { logos: [...logos, { url: '', orientation: 'horizontal', variant: 'primary', background: 'transparent-bg' }] })}
            className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium">
            <Plus className="w-3 h-3" /> Add Logo
          </button>
        </div>
        {logos.length === 0 && <p className="text-sm text-gray-400">No logos added yet.</p>}
        {logos.map((logo, li) => (
          <div key={li} className="p-3 border border-gray-200 dark:border-gray-800 rounded-lg mb-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500">Logo {li + 1}</span>
              <button type="button" onClick={() => updateBrand(0, { logos: logos.filter((_, idx) => idx !== li) })} className="text-red-500 hover:text-red-600">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <Field label="URL" value={logo.url} onChange={(v) => { const u = [...logos]; u[li] = { ...u[li], url: v }; updateBrand(0, { logos: u }) }} placeholder="https://cdn.example.com/logo.png" disabled={isLoading} />
            <div className="grid grid-cols-3 gap-3">
              <SelectField label="Orientation" value={logo.orientation || 'horizontal'} onChange={(v) => { const u = [...logos]; u[li] = { ...u[li], orientation: v as any }; updateBrand(0, { logos: u }) }}
                options={[{ value: 'square', label: 'Square' }, { value: 'horizontal', label: 'Horizontal' }, { value: 'vertical', label: 'Vertical' }, { value: 'stacked', label: 'Stacked' }]} />
              <SelectField label="Background" value={logo.background || 'transparent-bg'} onChange={(v) => { const u = [...logos]; u[li] = { ...u[li], background: v as any }; updateBrand(0, { logos: u }) }}
                options={[{ value: 'dark-bg', label: 'Dark BG' }, { value: 'light-bg', label: 'Light BG' }, { value: 'transparent-bg', label: 'Transparent' }]} />
              <SelectField label="Variant" value={logo.variant || 'primary'} onChange={(v) => { const u = [...logos]; u[li] = { ...u[li], variant: v as any }; updateBrand(0, { logos: u }) }}
                options={[{ value: 'primary', label: 'Primary' }, { value: 'secondary', label: 'Secondary' }, { value: 'icon', label: 'Icon' }, { value: 'wordmark', label: 'Wordmark' }, { value: 'full-lockup', label: 'Full Lockup' }]} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Width" value={String(logo.width ?? '')} onChange={(v) => { const u = [...logos]; u[li] = { ...u[li], width: v ? parseInt(v) : undefined }; updateBrand(0, { logos: u }) }} placeholder="e.g. 600" disabled={isLoading} />
              <Field label="Height" value={String(logo.height ?? '')} onChange={(v) => { const u = [...logos]; u[li] = { ...u[li], height: v ? parseInt(v) : undefined }; updateBrand(0, { logos: u }) }} placeholder="e.g. 200" disabled={isLoading} />
            </div>
            <Field label="Usage Note" value={logo.usage || ''} onChange={(v) => { const u = [...logos]; u[li] = { ...u[li], usage: v }; updateBrand(0, { logos: u }) }} placeholder="e.g. Primary logo for light backgrounds" disabled={isLoading} />
            <TagInput label="Tags" values={logo.tags || []} onChange={(v) => { const u = [...logos]; u[li] = { ...u[li], tags: v }; updateBrand(0, { logos: u }) }} placeholder="e.g. spencerian" />
          </div>
        ))}
      </div>
    </div>
  )

  const renderToneVoice = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Tone & Voice</h3>
      <Field label="Voice" value={tone.voice || ''} onChange={(v) => updateBrandTone({ voice: v })} placeholder="e.g. warm and inviting, professional" disabled={isLoading} />
      <TagInput label="Attributes" values={tone.attributes || []} onChange={(v) => updateBrandTone({ attributes: v })} placeholder="e.g. reliable" />
      <TagInput label="Do's" values={tone.dos || []} onChange={(v) => updateBrandTone({ dos: v })} placeholder="e.g. Use simple language" />
      <TagInput label="Don'ts" values={tone.donts || []} onChange={(v) => updateBrandTone({ donts: v })} placeholder="e.g. Avoid jargon" />
    </div>
  )

  const renderDigitalProperties = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Digital Properties</h3>
        <button type="button" onClick={() => updateBrand(0, { properties: [...properties, { type: 'website', identifier: '', primary: false }] })}
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium">
          <Plus className="w-3 h-3" /> Add Property
        </button>
      </div>
      {properties.length === 0 && <p className="text-sm text-gray-400">No properties added yet.</p>}
      {properties.map((prop, pi) => (
        <div key={pi} className="flex items-start gap-3 p-3 border border-gray-200 dark:border-gray-800 rounded-lg">
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <SelectField label="Type" value={prop.type} onChange={(v) => { const u = [...properties]; u[pi] = { ...u[pi], type: v as any }; updateBrand(0, { properties: u }) }}
              options={[
                { value: 'website', label: 'Website' }, { value: 'mobile_app', label: 'Mobile App' }, { value: 'ctv_app', label: 'CTV App' },
                { value: 'desktop_app', label: 'Desktop App' }, { value: 'dooh', label: 'DOOH' }, { value: 'podcast', label: 'Podcast' },
                { value: 'radio', label: 'Radio' }, { value: 'streaming_audio', label: 'Streaming Audio' },
              ]} />
            <Field label="Identifier" value={prop.identifier} onChange={(v) => { const u = [...properties]; u[pi] = { ...u[pi], identifier: v }; updateBrand(0, { properties: u }) }} placeholder="e.g. nike.com" disabled={isLoading} />
            {(prop.type === 'mobile_app' || prop.type === 'ctv_app') && (
              <SelectField label="Store" value={prop.store || 'apple'} onChange={(v) => { const u = [...properties]; u[pi] = { ...u[pi], store: v as any }; updateBrand(0, { properties: u }) }}
                options={[{ value: 'apple', label: 'Apple' }, { value: 'google', label: 'Google' }, { value: 'amazon', label: 'Amazon' }, { value: 'roku', label: 'Roku' }, { value: 'samsung', label: 'Samsung' }, { value: 'lg', label: 'LG' }, { value: 'other', label: 'Other' }]} />
            )}
          </div>
          <div className="flex items-center gap-3 pt-6">
            <label className="flex items-center gap-1.5 text-xs text-gray-500">
              <input type="checkbox" checked={prop.primary || false} onChange={(e) => { const u = [...properties]; u[pi] = { ...u[pi], primary: e.target.checked }; updateBrand(0, { properties: u }) }} className="rounded border-gray-300" />
              Primary
            </label>
            <button type="button" onClick={() => updateBrand(0, { properties: properties.filter((_, idx) => idx !== pi) })} className="text-red-500 hover:text-red-600">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  )

  const renderProductCatalog = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Product Catalog</h3>
      <Field label="Feed URL" value={brand.product_catalog?.feed_url || ''} onChange={(v) => updateBrand(0, { product_catalog: { ...brand.product_catalog, feed_url: v } as any })} placeholder="https://feeds.example.com/products.xml" disabled={isLoading} />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SelectField label="Feed Format" value={brand.product_catalog?.feed_format || 'google_merchant_center'} onChange={(v) => updateBrand(0, { product_catalog: { ...brand.product_catalog, feed_url: brand.product_catalog?.feed_url || '', feed_format: v as any } })}
          options={[{ value: 'google_merchant_center', label: 'Google Merchant Center' }, { value: 'facebook_catalog', label: 'Facebook Catalog' }, { value: 'openai_product_feed', label: 'OpenAI Product Feed' }, { value: 'custom', label: 'Custom' }]} />
        <SelectField label="Update Frequency" value={brand.product_catalog?.update_frequency || 'daily'} onChange={(v) => updateBrand(0, { product_catalog: { ...brand.product_catalog, feed_url: brand.product_catalog?.feed_url || '', update_frequency: v as any } })}
          options={[{ value: 'realtime', label: 'Real-time' }, { value: 'hourly', label: 'Hourly' }, { value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }]} />
      </div>
      <TagInput label="Categories" values={brand.product_catalog?.categories || []} onChange={(v) => updateBrand(0, { product_catalog: { ...brand.product_catalog, feed_url: brand.product_catalog?.feed_url || '', categories: v } })} placeholder="e.g. footwear" />
    </div>
  )

  const renderLegal = () => (
    <div className="space-y-5">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Legal & Compliance</h3>
      <Field label="Privacy Policy URL" value={brand.privacy_policy_url || ''} onChange={(v) => updateBrand(0, { privacy_policy_url: v })} placeholder="https://example.com/privacy" disabled={isLoading} />

      {/* Disclaimers */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Disclaimers</label>
          <button type="button" onClick={() => updateBrand(0, { disclaimers: [...disclaimers, { text: '', context: '', required: true }] })}
            className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"><Plus className="w-3 h-3" /> Add</button>
        </div>
        {disclaimers.map((d, di) => (
          <div key={di} className="flex items-start gap-3 mb-2 p-3 border border-gray-200 dark:border-gray-800 rounded-lg">
            <div className="flex-1 space-y-2">
              <Field label="Text" value={d.text} onChange={(v) => { const u = [...disclaimers]; u[di] = { ...u[di], text: v }; updateBrand(0, { disclaimers: u }) }} placeholder="Disclaimer text" disabled={isLoading} />
              <Field label="Context" value={d.context || ''} onChange={(v) => { const u = [...disclaimers]; u[di] = { ...u[di], context: v }; updateBrand(0, { disclaimers: u }) }} placeholder="When to show this disclaimer" disabled={isLoading} />
            </div>
            <button type="button" onClick={() => updateBrand(0, { disclaimers: disclaimers.filter((_, idx) => idx !== di) })} className="text-red-500 hover:text-red-600 mt-6"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      {/* Trademarks */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Trademarks</label>
          <button type="button" onClick={() => setFormData((p) => ({ ...p, trademarks: [...trademarks, { registry: '', number: '', mark: '' }] }))}
            className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium"><Plus className="w-3 h-3" /> Add</button>
        </div>
        {trademarks.map((t, ti) => (
          <div key={ti} className="flex items-start gap-3 mb-2 p-3 border border-gray-200 dark:border-gray-800 rounded-lg">
            <div className="flex-1 grid grid-cols-3 gap-3">
              <Field label="Registry" value={t.registry} onChange={(v) => { const u = [...trademarks]; u[ti] = { ...u[ti], registry: v }; setFormData((p) => ({ ...p, trademarks: u })) }} placeholder="USPTO" disabled={isLoading} />
              <Field label="Number" value={t.number} onChange={(v) => { const u = [...trademarks]; u[ti] = { ...u[ti], number: v }; setFormData((p) => ({ ...p, trademarks: u })) }} placeholder="123456" disabled={isLoading} />
              <Field label="Mark" value={t.mark} onChange={(v) => { const u = [...trademarks]; u[ti] = { ...u[ti], mark: v }; setFormData((p) => ({ ...p, trademarks: u })) }} placeholder="NIKE" disabled={isLoading} />
            </div>
            <button type="button" onClick={() => setFormData((p) => ({ ...p, trademarks: trademarks.filter((_, idx) => idx !== ti) }))} className="text-red-500 hover:text-red-600 mt-6"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  )

  const renderContact = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Contact</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Contact Name" value={formData.contact?.name || ''} onChange={(v) => setFormData((p) => ({ ...p, contact: { ...p.contact, name: v } }))} placeholder="Brand Team" disabled={isLoading} />
        <Field label="Contact Email" value={formData.contact?.email || ''} onChange={(v) => setFormData((p) => ({ ...p, contact: { ...p.contact, email: v } }))} placeholder="brands@example.com" type="email" disabled={isLoading} />
      </div>
    </div>
  )

  const renderVisualGuidelines = () => {
    const vg = getVisualGuidelines()
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Visual Guidelines</h3>

        {/* Photography */}
        <div className="space-y-3">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Photography</label>
          <Field label="Style" value={vg.photography?.style || ''} onChange={(v) => updateVisualGuidelines({ photography: { ...vg.photography, style: v } })} placeholder="e.g. Warm, saturated, naturalistic" disabled={isLoading} />
          <TagInput label="Subjects" values={vg.photography?.subjects || []} onChange={(v) => updateVisualGuidelines({ photography: { ...vg.photography, subjects: v } })} placeholder="e.g. friends sharing a meal" />
          <TagInput label="Forbidden" values={vg.photography?.forbidden || []} onChange={(v) => updateVisualGuidelines({ photography: { ...vg.photography, forbidden: v } })} placeholder="e.g. isolated product-only shots" />
        </div>

        {/* Graphic Style */}
        <div className="space-y-3">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Graphic Style</label>
          <Field label="Approach" value={vg.graphic_style?.approach || ''} onChange={(v) => updateVisualGuidelines({ graphic_style: { ...vg.graphic_style, approach: v } })} placeholder="e.g. Bold, flat, iconic" disabled={isLoading} />
          <Field label="Motion Guidelines" value={vg.graphic_style?.motion_guidelines || ''} onChange={(v) => updateVisualGuidelines({ graphic_style: { ...vg.graphic_style, motion_guidelines: v } })} placeholder="e.g. Liquid pour effects, carbonation bubbles" disabled={isLoading} />
        </div>

        {/* Colorways */}
        <div className="space-y-3">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Colorways</label>
          <TagInput label="Primary Palette" values={vg.colorways?.primary_palette || []} onChange={(v) => updateVisualGuidelines({ colorways: { ...vg.colorways, primary_palette: v } })} placeholder="e.g. #F40009" />
          <TagInput label="Accent Palette" values={vg.colorways?.accent_palette || []} onChange={(v) => updateVisualGuidelines({ colorways: { ...vg.colorways, accent_palette: v } })} placeholder="e.g. #C8001A" />
          <Field label="Usage" value={vg.colorways?.usage || ''} onChange={(v) => updateVisualGuidelines({ colorways: { ...vg.colorways, usage: v } })} placeholder="e.g. Red dominates all brand surfaces" disabled={isLoading} />
        </div>

        {/* Type Scale */}
        <div className="space-y-3">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Type Scale</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Display" value={vg.type_scale?.display || ''} onChange={(v) => updateVisualGuidelines({ type_scale: { ...vg.type_scale, display: v } })} placeholder="e.g. Spencerian Script — logotype use only" disabled={isLoading} />
            <Field label="Headline" value={vg.type_scale?.headline || ''} onChange={(v) => updateVisualGuidelines({ type_scale: { ...vg.type_scale, headline: v } })} placeholder="e.g. Gotham Bold — subheadlines" disabled={isLoading} />
            <Field label="Body" value={vg.type_scale?.body || ''} onChange={(v) => updateVisualGuidelines({ type_scale: { ...vg.type_scale, body: v } })} placeholder="e.g. Gotham Book — marketing copy" disabled={isLoading} />
            <Field label="Fallback" value={vg.type_scale?.fallback || ''} onChange={(v) => updateVisualGuidelines({ type_scale: { ...vg.type_scale, fallback: v } })} placeholder="e.g. Arial — internal only" disabled={isLoading} />
          </div>
        </div>

        {/* Motion */}
        <div className="space-y-3">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Motion</label>
          <Field label="Easing" value={vg.motion?.easing || ''} onChange={(v) => updateVisualGuidelines({ motion: { ...vg.motion, easing: v } })} placeholder="e.g. ease-in-out" disabled={isLoading} />
          <TagInput label="Signature Elements" values={vg.motion?.signature_elements || []} onChange={(v) => updateVisualGuidelines({ motion: { ...vg.motion, signature_elements: v } })} placeholder="e.g. liquid pour" />
          <Field label="Duration Guidelines" value={vg.motion?.duration_guidelines || ''} onChange={(v) => updateVisualGuidelines({ motion: { ...vg.motion, duration_guidelines: v } })} placeholder="e.g. Hero animations 1.5–3s" disabled={isLoading} />
        </div>
      </div>
    )
  }

  const renderVoiceSynthesis = () => {
    const vs = getVoiceSynthesis()
    const settings = vs.settings || {}
    const updateSettings = (patch: Record<string, any>) =>
      updateVoiceSynthesis({ settings: { ...settings, ...patch } })

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Voice Synthesis</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Provider" value={vs.provider || ''} onChange={(v) => updateVoiceSynthesis({ provider: v })} placeholder="e.g. elevenlabs" disabled={isLoading} />
          <Field label="Voice ID" value={vs.voice_id || ''} onChange={(v) => updateVoiceSynthesis({ voice_id: v })} placeholder="e.g. CzIaVuZbYEFXYxeVp7YN" disabled={isLoading} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Field label="Gender" value={settings.gender || ''} onChange={(v) => updateSettings({ gender: v })} placeholder="e.g. neutral" disabled={isLoading} />
          <Field label="Age Range" value={settings.age_range || ''} onChange={(v) => updateSettings({ age_range: v })} placeholder="e.g. 25–40" disabled={isLoading} />
          <Field label="Accent" value={settings.accent || ''} onChange={(v) => updateSettings({ accent: v })} placeholder="e.g. General American" disabled={isLoading} />
        </div>
        <TagInput label="Tone Descriptors" values={settings.tone_descriptors || []} onChange={(v) => updateSettings({ tone_descriptors: v })} placeholder="e.g. warm" />
        <div className="grid grid-cols-3 gap-4">
          <Field label="Stability" value={String(settings.stability ?? '')} onChange={(v) => updateSettings({ stability: v ? parseFloat(v) : undefined })} placeholder="0.72" disabled={isLoading} />
          <Field label="Similarity Boost" value={String(settings.similarity_boost ?? '')} onChange={(v) => updateSettings({ similarity_boost: v ? parseFloat(v) : undefined })} placeholder="0.85" disabled={isLoading} />
          <Field label="Style" value={String(settings.style ?? '')} onChange={(v) => updateSettings({ style: v ? parseFloat(v) : undefined })} placeholder="0.45" disabled={isLoading} />
        </div>
      </div>
    )
  }

  const renderAssets = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Assets</h3>
        <button type="button" onClick={() => updateBrand(0, { assets: [...assets, { asset_id: '', asset_type: '', url: '' }] })}
          className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium">
          <Plus className="w-3 h-3" /> Add Asset
        </button>
      </div>
      {assets.length === 0 && <p className="text-sm text-gray-400">No assets added yet.</p>}
      {assets.map((asset, ai) => (
        <div key={ai} className="p-3 border border-gray-200 dark:border-gray-800 rounded-lg space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500">Asset {ai + 1}</span>
            <button type="button" onClick={() => updateBrand(0, { assets: assets.filter((_, idx) => idx !== ai) })} className="text-red-500 hover:text-red-600">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Name" value={asset.name || ''} onChange={(v) => { const u = [...assets]; u[ai] = { ...u[ai], name: v }; updateBrand(0, { assets: u }) }} placeholder="e.g. Hero product shot" disabled={isLoading} />
            <Field label="Asset ID" value={asset.asset_id} onChange={(v) => { const u = [...assets]; u[ai] = { ...u[ai], asset_id: v }; updateBrand(0, { assets: u }) }} placeholder="e.g. asset_001" disabled={isLoading} />
          </div>
          <Field label="URL" value={asset.url} onChange={(v) => { const u = [...assets]; u[ai] = { ...u[ai], url: v }; updateBrand(0, { assets: u }) }} placeholder="https://assets.example.com/image.jpg" disabled={isLoading} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Field label="Type" value={asset.asset_type} onChange={(v) => { const u = [...assets]; u[ai] = { ...u[ai], asset_type: v }; updateBrand(0, { assets: u }) }} placeholder="e.g. image/jpeg" disabled={isLoading} />
            <Field label="Format" value={asset.format || ''} onChange={(v) => { const u = [...assets]; u[ai] = { ...u[ai], format: v }; updateBrand(0, { assets: u }) }} placeholder="e.g. jpg" disabled={isLoading} />
            <Field label="Width" value={String(asset.width ?? '')} onChange={(v) => { const u = [...assets]; u[ai] = { ...u[ai], width: v ? parseInt(v) : undefined }; updateBrand(0, { assets: u }) }} placeholder="3840" disabled={isLoading} />
            <Field label="Height" value={String(asset.height ?? '')} onChange={(v) => { const u = [...assets]; u[ai] = { ...u[ai], height: v ? parseInt(v) : undefined }; updateBrand(0, { assets: u }) }} placeholder="2160" disabled={isLoading} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <textarea value={asset.description || ''} onChange={(e) => { const u = [...assets]; u[ai] = { ...u[ai], description: e.target.value }; updateBrand(0, { assets: u }) }} placeholder="Asset description..." rows={2} disabled={isLoading}
              className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors disabled:opacity-50 resize-none" />
          </div>
          <TagInput label="Tags" values={asset.tags || []} onChange={(v) => { const u = [...assets]; u[ai] = { ...u[ai], tags: v }; updateBrand(0, { assets: u }) }} placeholder="e.g. hero" />
        </div>
      ))}
    </div>
  )

  const renderRights = () => {
    const r = getRights()
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Rights & Usage</h3>
        <SelectField label="Exclusivity Model" value={r.exclusivity_model || 'non-exclusive'} onChange={(v) => updateRights({ exclusivity_model: v })}
          options={[{ value: 'non-exclusive', label: 'Non-Exclusive' }, { value: 'exclusive', label: 'Exclusive' }, { value: 'semi-exclusive', label: 'Semi-Exclusive' }]} />
        <TagInput label="Available Uses" values={r.available_uses || []} onChange={(v) => updateRights({ available_uses: v })} placeholder="e.g. digital_advertising" />
        <TagInput label="Countries" values={r.countries || []} onChange={(v) => updateRights({ countries: v })} placeholder="e.g. US" />
        <TagInput label="Excluded Countries" values={r.excluded_countries || []} onChange={(v) => updateRights({ excluded_countries: v })} placeholder="e.g. KP" />
        <TagInput label="Content Restrictions" values={r.content_restrictions || []} onChange={(v) => updateRights({ content_restrictions: v })} placeholder="e.g. No association with alcohol" />
      </div>
    )
  }

  const tabContent: Record<string, () => React.ReactNode> = {
    org_access: renderOrgAccess,
    house: renderHouse,
    brand_identity: renderBrandIdentity,
    visual_identity: renderVisualIdentity,
    visual_guidelines: renderVisualGuidelines,
    tone_voice: renderToneVoice,
    voice_synthesis: renderVoiceSynthesis,
    assets: renderAssets,
    digital_properties: renderDigitalProperties,
    product_catalog: renderProductCatalog,
    legal: renderLegal,
    rights: renderRights,
    contact: renderContact,
  }

  /* ================================================================ */
  /*  Render                                                           */
  /* ================================================================ */
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm" onClick={handleClose} />

      <div className="relative bg-white dark:bg-gray-950 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white overflow-hidden" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="relative flex-shrink-0 px-6 pt-6 pb-5 border-b border-gray-200 dark:border-gray-800 bg-gradient-to-b from-primary-100 via-white to-white dark:from-primary-900/40 dark:via-gray-950 dark:to-gray-950">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold tracking-[0.18em] text-primary-600 dark:text-primary-400 uppercase flex items-center gap-1.5">
                <Sparkles className="h-3 w-3" />
                Brand Kit
              </div>
              <h2 className="text-2xl font-bold mt-1">
                {isEditMode ? 'Edit brand kit' : 'Create brand kit'}
              </h2>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {isEditMode
                  ? 'Update the brand identity, visual guidelines, and rights for this kit.'
                  : 'Set up a new brand kit by filling in the details across each section.'}
              </p>
            </div>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="p-2 rounded-full bg-white dark:bg-gray-900/80 border border-gray-200 dark:border-gray-800 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 shadow-sm"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Import banner */}
        <div className={`px-6 py-3 border-b flex-shrink-0 transition-colors ${
          importedFileName
            ? 'border-green-200 dark:border-green-800/50 bg-green-50 dark:bg-green-900/10'
            : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40'
        }`}>
          <input ref={fileInputRef} type="file" accept=".json,.txt" onChange={handleFileUpload} className="hidden" />
          {importedFileName ? (
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-green-700 dark:text-green-400">
                  Imported from <span className="font-semibold">{importedFileName}</span>
                </p>
                <p className="text-xs text-green-600 dark:text-green-500 mt-0.5">
                  All fields have been auto-filled. Review each tab before saving.
                </p>
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-1.5 text-xs bg-green-100 dark:bg-green-800/40 hover:bg-green-200 dark:hover:bg-green-800/60 text-green-700 dark:text-green-300 rounded-lg transition-colors font-medium whitespace-nowrap"
              >
                Re-upload
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <FileText className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  Upload a <strong>brand.json</strong> to auto-fill all fields
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Supports AdCP v3 brand schema. All matching fields will be populated.
                </p>
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors font-medium whitespace-nowrap"
              >
                <Upload className="w-4 h-4" />
                Choose File
              </button>
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="px-6 py-2 flex-shrink-0">
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Vertical tab layout */}
        <form onSubmit={handleSubmit} className="flex h-[60vh] min-h-0">
          {/* Left: tab list */}
          <div className="w-52 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 overflow-y-auto bg-gray-50 dark:bg-gray-900/40 py-2">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white dark:bg-gray-900 text-primary-700 dark:text-primary-400 font-medium border-l-2 border-primary-600'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200 border-l-2 border-transparent'
                }`}
              >
                {tab.icon}
                <span className="truncate">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Right: tab content */}
          <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
            {tabContent[activeTab]?.()}
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 flex-shrink-0">
          <button type="button" onClick={handleClose} disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={isLoading || !formData.name.trim()}
            className="px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-2">
            {isLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
            {isLoading ? (isEditMode ? 'Saving...' : 'Creating...') : isEditMode ? 'Save Changes' : 'Create Brand Kit'}
          </button>
        </div>
      </div>
    </div>
  )
}
